import { useEffect, useState } from 'react';
import { useTranslation } from '@/lib/i18n';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  RadioGroup,
} from '@/components/ui';
import type { KeyTarget } from '../types/openApiPartner';

const REASON_MAX = 500;

const ErrorBox = ({ message }: { message: string | null | undefined }) =>
  message ? (
    <p role="alert" className="rounded px-3 py-2 text-[13px]" style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}>
      {message}
    </p>
  ) : null;

interface ReissueDialogProps {
  open: boolean;
  partnerName: string;
  submitting: boolean;
  errorMessage?: string | null;
  onSubmit: (target: KeyTarget, reason: string | null) => void;
  onClose: () => void;
}

/** 키 재발급 — 대상 선택 + 사유 → 확인 → 실행. 재발급 즉시 옛 키는 못 쓴다(998/999). */
export const ReissueKeysDialog = ({ open, partnerName, submitting, errorMessage, onSubmit, onClose }: ReissueDialogProps) => {
  const { t } = useTranslation('common');
  const targetLabel = (k: KeyTarget) =>
    k === 'PARTNER_KEY'
      ? t('openApiPartners.reissue.partnerKey', '제휴사 키')
      : k === 'SHOP_KEY'
        ? t('openApiPartners.reissue.shopKey', '쇼핑몰 키')
        : t('openApiPartners.reissue.bothKeys', '제휴사 키와 쇼핑몰 키');
  const [target, setTarget] = useState<KeyTarget>('BOTH');
  const [reason, setReason] = useState('');
  // 실행 전 확인 단계 — 옛 키가 즉시 끊기므로 한 번 더 묻는다.
  const [confirming, setConfirming] = useState(false);
  useEffect(() => {
    if (open) {
      setTarget('BOTH');
      setReason('');
      setConfirming(false);
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => (!o && !submitting ? onClose() : undefined)}>
      <DialogContent className="max-w-[480px]" data-testid="reissue-keys-dialog">
        <DialogHeader>
          <DialogTitle>{t('openApiPartners.reissue.title', '키 재발급')}</DialogTitle>
          <DialogDescription>{partnerName}</DialogDescription>
        </DialogHeader>
        <p role="alert" className="rounded px-3 py-2 text-[13px]" style={{ background: 'var(--status-warn-bg)', color: 'var(--status-warn-fg)' }}>
          {t(
            'openApiPartners.reissue.warning',
            '재발급 즉시 기존 키는 사용할 수 없습니다. 수집 업체에 새 키를 전달해야 합니다.',
          )}
        </p>
        {confirming ? (
          <div className="flex flex-col gap-2 py-1" data-testid="reissue-confirm">
            <p className="text-[14px] font-medium" style={{ color: 'var(--content-strong)' }}>
              {t('openApiPartners.reissue.confirm', {
                target: targetLabel(target),
                defaultValue: '{{target}}를 지금 재발급할까요?',
              })}
            </p>
            <ErrorBox message={errorMessage} />
          </div>
        ) : (
        <div className="flex flex-col gap-3 py-1">
          <RadioGroup
            value={target}
            onValueChange={(v) => setTarget(v as KeyTarget)}
            aria-label={t('openApiPartners.reissue.target', '재발급 대상')}
            options={[
              { value: 'PARTNER_KEY', label: t('openApiPartners.reissue.partnerKey', '제휴사 키') },
              { value: 'SHOP_KEY', label: t('openApiPartners.reissue.shopKey', '쇼핑몰 키') },
              { value: 'BOTH', label: t('openApiPartners.reissue.both', '둘 다') },
            ]}
          />
          <label className="grid gap-1 text-[13px]">
            <span style={{ color: 'var(--content-muted)' }}>{t('openApiPartners.fields.reason', '사유')}</span>
            <Input
              value={reason}
              maxLength={REASON_MAX}
              placeholder={t('openApiPartners.hints.reason', '이력에 남습니다. (선택)')}
              onChange={(e) => setReason(e.target.value)}
            />
          </label>
          <ErrorBox message={errorMessage} />
        </div>
        )}
        <DialogFooter>
          {confirming ? (
            <>
              <Button type="button" variant="outline" onClick={() => setConfirming(false)} disabled={submitting}>
                {t('openApiPartners.actions.back', '이전')}
              </Button>
              <Button type="button" variant="danger" disabled={submitting} onClick={() => onSubmit(target, reason.trim() || null)}>
                {t('openApiPartners.reissue.submit', '재발급')}
              </Button>
            </>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
                {t('openApiPartners.actions.cancel', '취소')}
              </Button>
              <Button type="button" variant="danger" onClick={() => setConfirming(true)}>
                {t('openApiPartners.reissue.next', '다음')}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface DeleteDialogProps {
  open: boolean;
  partnerName: string;
  submitting: boolean;
  errorMessage?: string | null;
  onSubmit: (reason: string | null) => void;
  onClose: () => void;
}

/** 삭제 확인 + 사유. 소프트 삭제(이력은 남음), 즉시 호출 불가. */
export const DeletePartnerDialog = ({ open, partnerName, submitting, errorMessage, onSubmit, onClose }: DeleteDialogProps) => {
  const { t } = useTranslation('common');
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) setReason('');
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => (!o && !submitting ? onClose() : undefined)}>
      <DialogContent className="max-w-[480px]" data-testid="delete-partner-dialog">
        <DialogHeader>
          <DialogTitle>{t('openApiPartners.delete.title', '제휴사 삭제')}</DialogTitle>
          <DialogDescription>
            {t('openApiPartners.delete.confirm', {
              name: partnerName,
              defaultValue: '"{{name}}" 을(를) 삭제할까요? 삭제하면 바로 호출할 수 없고 되돌릴 수 없습니다. 변경 이력은 남습니다.',
            })}
          </DialogDescription>
        </DialogHeader>
        <label className="grid gap-1 text-[13px]">
          <span style={{ color: 'var(--content-muted)' }}>{t('openApiPartners.fields.reason', '사유')}</span>
          <Input
            value={reason}
            maxLength={REASON_MAX}
            placeholder={t('openApiPartners.hints.reason', '이력에 남습니다. (선택)')}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        <ErrorBox message={errorMessage} />
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={submitting}>
            {t('openApiPartners.actions.cancel', '취소')}
          </Button>
          <Button type="button" variant="danger" disabled={submitting} onClick={() => onSubmit(reason.trim() || null)}>
            {t('openApiPartners.delete.submit', '삭제')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
